import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Order from '../../models/Order';
import { UnderlingUser } from '../../models/SystemTables';
import { UserOrdersResponse } from '../../models/UserOrdersResponse';
import { GetUnderlingUserOrdersParams } from '../../models/GetUnderlingUserOrdersParams';
import { getUnderlingUserOrders } from '../../services/ApiService';
import { useSystemTables } from '../../hooks/UseSystemTables';
import UnderlingUsersSideMenu from '../../components/UnderlingUsersSideMenu/UnderlingUsersSideMenu';
import OrderCard from '../../components/OrderCard/OrderCard';
import Spinner from '../../components/Spinner/Spinner';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Pagination as MuiPagination,
} from '@mui/material';
import styles from './OrderHistory.module.css';

const OrderHistory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [underlingUsers, setUnderlingUsers] = useState<UnderlingUser[]>([]);
  const [selectedUnderlingUser, setSelectedUnderlingUser] = useState<UnderlingUser | null>(null);
  const [page, setPage] = useState<number>(1);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [origin, setOrigin] = useState<string | undefined>();
  const [status, setStatus] = useState<number | undefined>();
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [transportationCompany, setTransportationCompany] = useState<string | undefined>();

  const { data: systemTablesData, isLoading: tablesLoading } = useSystemTables();

  useEffect(() => {
    if (systemTablesData?.data?.underlingUsers) {
      setUnderlingUsers(systemTablesData.data.underlingUsers);
    }
  }, [systemTablesData]);

  useEffect(() => {
    if (underlingUsers.length > 0 && !selectedUnderlingUser) {
      setSelectedUnderlingUser(underlingUsers[0]);
    }
  }, [underlingUsers, selectedUnderlingUser]);

  const filters: GetUnderlingUserOrdersParams = {
    clientInnerId: selectedUnderlingUser?.attributes['custom:clientInnerId'],
    page,
    limit: 8,
    origin,
    status,
    startDate,
    endDate,
    transportationCompany,
  };

  const {
    data: userOrdersData,
    refetch: refetchOrders,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useQuery<UserOrdersResponse>({
    queryKey: ['orders', selectedUnderlingUser?.username, page],
    queryFn: () => getUnderlingUserOrders(filters),
    enabled: !!selectedUnderlingUser,
  });

  const orders: Order[] = userOrdersData?.orders || [];
  const totalPages: number = userOrdersData?.totalPages || 1;
  const currentPage: number = userOrdersData?.page || 1;

  const handleUserClick = (user: UnderlingUser) => {
    setSelectedUnderlingUser(user);
    setPage(1);
  };

  const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    refetchOrders();
  };

  const allLabel = t('General.Labels.All');

  const originOptions = systemTablesData?.data?.warehouseLocations?.map((location) => ({
    value: location._id,
    label: i18n.language === 'he' ? location?.nameHeb : location?.nameEng,
  })) || [];
  originOptions.unshift({ value: '', label: allLabel });

  const statusOptions = systemTablesData?.data?.orderStatus?.map((status) => ({
    value: String(status._id),
    label: i18n.language === 'he' ? status?.name_hebrew : status?.name,
  })) || [];
  statusOptions.unshift({ value: '', label: allLabel });

  const transportationOptions = systemTablesData?.data?.transportCompanies?.map((company) => ({
    value: company._id,
    label: company.companyName ? company.companyName : company.companyInnerId,
  })) || [];
  transportationOptions.unshift({ value: '', label: allLabel });

  // Handler for TextField changes (e.g. for date fields)
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') {
      setStartDate(value || undefined);
    } else if (name === 'endDate') {
      setEndDate(value || undefined);
    }
  };

  // Handler for Select components
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name === 'origin') {
      setOrigin(value || undefined);
    } else if (name === 'status') {
      setStatus(value === '' ? undefined : Number(value));
    } else if (name === 'transportationCompany') {
      setTransportationCompany(value || undefined);
    }
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('OrderHistory.Title')}</h2>
      <div className={styles.contentWrapper}>
        <Box>
          <UnderlingUsersSideMenu
            underlingUsers={underlingUsers}
            selectedUnderlingUser={selectedUnderlingUser}
            onUserClick={handleUserClick}
          />
        </Box>
        <Box className={styles.generalSection}>
          <Box mb={2}>
            <Button variant="contained" onClick={toggleFilter}>
              {isFilterOpen ? t('OrderHistory.Filter.Close') : t('OrderHistory.Filter.Open')}
            </Button>
          </Box>
          {isFilterOpen && (
            <Box component="form" onSubmit={handleFilterSubmit} mb={2}>
            <Grid container spacing={2}>
              {/* First Row: Non-date inputs */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="origin-label">{t('Order.Origin')}</InputLabel>
                  <Select
                    labelId="origin-label"
                    id="origin"
                    name="origin"
                    value={origin || ''}
                    label={t('Order.Origin')}
                    onChange={handleSelectChange}
                  >
                    {originOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">{t('Order.StatusTitle')}</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={status !== undefined ? String(status) : ''}
                    label={t('Order.StatusTitle')}
                    onChange={handleSelectChange}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="transportationCompany-label">
                    {t('Order.TransportationCompany')}
                  </InputLabel>
                  <Select
                    labelId="transportationCompany-label"
                    id="transportationCompany"
                    name="transportationCompany"
                    value={transportationCompany || ''}
                    label={t('Order.TransportationCompany')}
                    onChange={handleSelectChange}
                  >
                    {transportationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
          
              {/* Second Row: Date inputs */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      id="startDate"
                      name="startDate"
                      label={t('OrderHistory.Filter.Labels.StartDate')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={startDate || ''}
                      onChange={handleTextFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      id="endDate"
                      name="endDate"
                      label={t('OrderHistory.Filter.Labels.EndDate')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={endDate || ''}
                      onChange={handleTextFieldChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
          
              {/* Submit Button */}
              <Grid item xs={12}>
                <Button variant="contained" type="submit">
                  {t('OrderHistory.Filter.Apply')}
                </Button>
              </Grid>
            </Grid>
          </Box>
          )}
          <Box className={styles.ordersSection}>
            {(ordersLoading || tablesLoading || ordersFetching) && <Spinner />}
            {orders.map((order) => (
              <OrderCard key={order.orderId} order={{ ...order }} />
            ))}
          </Box>
          <Box display="flex" justifyContent="center" mt={2} dir="ltr">
            <MuiPagination
              page={currentPage}
              count={totalPages}
              onChange={handlePaginationChange}
              color="primary"
            />
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default OrderHistory;